import { useMemo, useState, useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { State, City } from "country-state-city";
import { useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  netural400,
  neturalBlack,
  primaryGrey,
  secondary500,
} from "../../../constants/color";
import DraftBanner from "../../../components/draft-banner/draft-banner.component";
import WheelPreview from "../../../components/wheel-preview/wheel-preview.component";
import RewardTypePicker from "../../../components/reward-type-picker/reward-type-picker.component";
import SliceConfigurationRow from "../../../components/slice-configuration-row/slice-configuration-row.component";
import ProductSelectionModal from "../../../components/product-selection-modal/product-selection-modal.component";
import WheelSectionCard from "../../../components/wheel-section-card/wheel-section-card.component";
import DuplicateToggle from "../../../components/duplicate-toggle/duplicate-toggle.component";
import SimpleGoogleAutocomplete from "../../../components/place-autocomplete/place-autocomplete.component";
import { CREATE_REWARD_WHEEL } from "../../../services/graphql/admin/rewardWheel";
import useSliceManager from "./useSliceManager";
import {
  SLICE_COLORS,
  DISPLAYED_SLICES_OPTIONS,
  TIMEZONE,
  detectPattern,
  getNextSunday,
  formatDateToISO,
  filterMondaysOnly,
  buildSliceInput,
  buildSlicesPayload,
  validateSlices,
  isSlicesValid,
  isZeroPricedSlice,
  formatValidationError,
} from "./rewardWheel.constants";
import AppInput from "../../../components/app-input.component/app-input.component";

const US_COUNTRY_CODE = "US";

const RewardWheelCreatePage = () => {
  const navigate = useNavigate();
  const businesprofile = useSelector((state) => state.businesprofile);

  // General settings
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Eligibility
  const [minimumPurchase, setMinimumPurchase] = useState("");

  // Location targeting
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [selectedStateLocation, setSelectedStateLocation] = useState(null);
  const [selectedCityLocation, setSelectedCityLocation] = useState(null);

  // Presentation / reward
  const [rewardType, setRewardType] = useState("Discount");

  // Validation errors
  const [errors, setErrors] = useState({});

  // Product selection modal
  const [modalSliceIndex, setModalSliceIndex] = useState(null);

  // Slice state via shared hook
  const {
    slices,
    displayedSlices,
    duplicateSlice,
    setDuplicateSlice,
    handleDisplayedSlicesChange,
    handleDuplicateToggle,
    handleSliceChange,
    handleProductSelected,
  } = useSliceManager(8, false);

  // Calculate if duplicate button should be disabled
  const calculateDuplicateDisabled = () => {
    // Count unique products
    const uniqueProductIds = new Set(
      slices
        .slice(0, displayedSlices)
        .filter((slice) => slice.productId)
        .map((slice) => slice.productId),
    );

    // Only disable when trying to turn it ON (not already checked)
    // Allow turning OFF even with 0 products to unlock fields
    if (duplicateSlice) {
      return false; // Always allow unchecking
    }

    // Disable if trying to turn it ON and:
    // 1. No products selected (0 unique products)
    // 2. All slices have different products (equal to displayedSlices)
    return (
      uniqueProductIds.size === 0 || uniqueProductIds.size === displayedSlices
    );
  };

  // Auto-uncheck duplicate if no products left
  useEffect(() => {
    if (duplicateSlice) {
      const uniqueProductIds = new Set(
        slices
          .slice(0, displayedSlices)
          .filter((slice) => slice.productId)
          .map((slice) => slice.productId),
      );

      if (uniqueProductIds.size === 0) {
        setDuplicateSlice(false);
      }
    }
  }, [slices, displayedSlices, duplicateSlice, setDuplicateSlice]);

  // Memoised location lists (for state code lookup)
  const states = useMemo(() => State.getStatesOfCountry(US_COUNTRY_CODE), []);

  // Handle State Selection
  const handleStateSelect = (locationData) => {
    if (!locationData) return;

    setSelectedStateLocation(locationData);
    const stateName = locationData.stateName?.trim() || "";

    if (stateName) {
      const stateObj = states.find((s) => s.name === stateName);
      if (stateObj) {
        console.log("Selected state:", stateObj);
        setState(stateObj.name);
      } else {
        setState("");
      }
    } else {
      setState("");
    }

    // Reset city when state changes
    setCity("");
    setSelectedCityLocation(null);

    if (errors.state) {
      setErrors((prev) => ({ ...prev, state: "", city: "" }));
    }
  };

  // Handle City Selection
  const handleCitySelect = (locationData) => {
    if (!locationData) return;

    setSelectedCityLocation(locationData);
    const cityName = locationData.cityName?.trim() || "";
    const stateName = locationData.stateName?.trim() || "";

    if (cityName) {
      setCity(cityName);
    } else {
      setCity("");
    }

    // Ensure state is also set if not already
    if (stateName && !state) {
      const stateObj = states.find((s) => s.name === stateName);
      if (stateObj) {
        setState(stateObj.isoCode);
      }
    }

    if (errors.city) {
      setErrors((prev) => ({ ...prev, city: "" }));
    }
  };

  // Mutation
  const [createRewardWheel, { loading: isSubmitting }] = useMutation(
    CREATE_REWARD_WHEEL,
    {
      fetchPolicy: "no-cache",
      onCompleted(data) {
        if (data?.createRewardWheel?.success) {
          toast.success(
            data.createRewardWheel.message || "Reward wheel created!",
            { position: "top-center", autoClose: 3000, theme: "colored" },
          );
          navigate("/admin/reward/list");
        } else {
          toast.error(
            data?.createRewardWheel?.message ||
              "Failed to create reward wheel.",
            { position: "top-center", autoClose: 3000, theme: "colored" },
          );
        }
      },
      onError(error) {
        // Format GraphQL validation errors into user-friendly messages
        // See formatValidationError() in rewardWheel.constants.js for customization
        const userFriendlyMessage = formatValidationError(error.message);
        toast.error(userFriendlyMessage, {
          position: "top-center",
          autoClose: 3000,
          theme: "colored",
        });
      },
    },
  );

  const handleStartDateChange = (date) => {
    setStartDate(date || null);
    setEndDate(date ? getNextSunday(date) : null);
    if (errors.startDate) setErrors((prev) => ({ ...prev, startDate: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!startDate) newErrors.startDate = "Start date is required";
    if (!endDate) newErrors.endDate = "End date is required";
    if (!minimumPurchase || Number(minimumPurchase) <= 0)
      newErrors.minimumPurchase = "Minimum purchase amount is required";
    if (!state) newErrors.state = "State is required";
    if (!city) newErrors.city = "City is required";

    const { sliceErrors, duplicateNoProduct } = validateSlices(
      slices,
      displayedSlices,
      duplicateSlice,
      rewardType,
    );
    if (duplicateNoProduct) newErrors.duplicateNoProduct = duplicateNoProduct;
    if (Object.keys(sliceErrors).length > 0) newErrors.slices = sliceErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    if (!startDate || !endDate || !minimumPurchase || !state || !city)
      return false;
    return isSlicesValid(slices, displayedSlices, duplicateSlice, rewardType);
  };

  const handlePublish = async () => {
    if (!validate() || isSubmitting) return;
    const slicesToSend = buildSlicesPayload(
      slices,
      displayedSlices,
      duplicateSlice,
    );
    const input = {
      startDate: formatDateToISO(startDate),
      timezone: TIMEZONE,
      minimumPurchase: parseFloat(minimumPurchase),
      state,
      city,
      displayedSlices,
      shouldDuplicate: duplicateSlice,
      rewardType,
      slices: slicesToSend.map((s) =>
        buildSliceInput(s, rewardType, businesprofile?.businessId),
      ),
    };
    // console.log("Submitting input:", input);
    await createRewardWheel({ variables: { input } });
  };

  const patternLength = detectPattern(slices, displayedSlices).length;

  return (
    <div className="main-div-right-section">
      <div className="second-section-style">
        {/* Breadcrumb */}
        <div style={{ marginBottom: "8px" }}>
          <Link
            to="/admin/reward/list"
            style={{
              fontSize: "16px",
              color: netural400,
              textDecoration: "underline",
            }}
          >
            Reward Wheel
          </Link>
          <label
            style={{ fontSize: "14px", color: netural400, margin: "0 8px" }}
          >
            &gt;
          </label>
          <label
            style={{ fontSize: "16px", color: neturalBlack, fontWeight: "600" }}
          >
            Create Game
          </label>
        </div>
        <label
          style={{
            fontSize: "22px",
            fontWeight: "bold",
            color: neturalBlack,
            marginBottom: "24px",
            display: "block",
          }}
        >
          Create Game
        </label>

        <DraftBanner
          title="Draft"
          subtitle="Complete all required information to submit your product to a wheel slice. Submitted setups can't be edited."
          actionLabel={isSubmitting ? "Publishing..." : "Publish game"}
          onAction={handlePublish}
          actionDisabled={!isFormValid() || isSubmitting}
        />

        <Row>
          {/* LEFT COLUMN */}
          <Col lg={6} md={6} style={{ paddingLeft: 0 }}>
            {/* General Settings */}
            <WheelSectionCard title="General Settings">
              <Row>
                <Col lg={6} md={6}>
                  <Form.Group>
                    <Form.Label
                      style={{ fontSize: "14px", color: neturalBlack }}
                    >
                      Start date
                    </Form.Label>
                    <DatePicker
                      selected={startDate}
                      onChange={handleStartDateChange}
                      filterDate={filterMondaysOnly}
                      minDate={new Date()}
                      dateFormat="yyyy-MM-dd"
                      className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                      placeholderText="Select Monday date"
                    />
                    {errors.startDate && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#EA1010",
                          marginTop: "4px",
                        }}
                      >
                        {errors.startDate}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col lg={6} md={6}>
                  <Form.Group>
                    <Form.Label
                      style={{ fontSize: "14px", color: neturalBlack }}
                    >
                      End date
                    </Form.Label>
                    <DatePicker
                      selected={endDate}
                      disabled
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div
                style={{
                  marginTop: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: secondary500,
                  }}
                />
                <label
                  style={{
                    fontSize: "13px",
                    color: secondary500,
                    fontWeight: "600",
                  }}
                >
                  Timezone: America/New York (Business Time)
                </label>
              </div>
              <label
                style={{
                  fontSize: "12px",
                  color: netural400,
                  marginTop: "4px",
                  display: "block",
                  paddingLeft: "16px",
                }}
              >
                If a campaign ends while a customer is at checkout, the reward
                remains valid until the redemption window expires.
              </label>
            </WheelSectionCard>

            {/* Eligibility Rules */}
            <WheelSectionCard
              title="Eligibility Rules"
              subtitle={"Minimum purchase amount required"}
            >
              <AppInput
                type="number"
                placeholder="5"
                value={minimumPurchase}
                min={0}
                onChange={(e) => setMinimumPurchase(e.target.value)}
                prefix={
                  <span
                    style={{
                      fontSize: "16px",
                      color: netural400,
                    }}
                  >
                    $
                  </span>
                }
                status={errors.minimumPurchase ? "error" : "normal"}
                helperText={errors.minimumPurchase}
                containerStyle={{
                  maxWidth: "200px",
                }}
              />
            </WheelSectionCard>

            {/* Location Targeting */}
            <WheelSectionCard
              title="Location Targeting"
              subtitle="Search and select a state, then select a city where customers must be located to see and redeem the reward."
            >
              {/* State Field */}
              <Form.Group style={{ marginBottom: "16px" }}>
                <Form.Label
                  style={{
                    fontSize: "14px",
                    color: neturalBlack,
                    marginBottom: "8px",
                  }}
                >
                  State
                </Form.Label>
                <SimpleGoogleAutocomplete
                  onSelect={handleStateSelect}
                  initialValue={selectedStateLocation}
                  countryCodes={["US"]}
                  showClearIcon={true}
                  showLabel={false}
                  searchType="state"
                  placeholder="Search and select a state"
                />
                {errors.state && (
                  <label
                    style={{
                      fontSize: "12px",
                      color: "#EA1010",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.state}
                  </label>
                )}
              </Form.Group>

              {/* City Field */}
              <Form.Group>
                <Form.Label
                  style={{
                    fontSize: "14px",
                    color: neturalBlack,
                    marginBottom: "8px",
                  }}
                >
                  City
                </Form.Label>
                <SimpleGoogleAutocomplete
                  onSelect={handleCitySelect}
                  countryCodes={["US"]}
                  initialValue={selectedCityLocation}
                  showLabel={false}
                  showClearIcon={true}
                  searchType="city"
                  placeholder={
                    state
                      ? "Search and select a city"
                      : "Please select a state first"
                  }
                  disabled={!state}
                  selectedState={state}
                />
                {errors.city && (
                  <label
                    style={{
                      fontSize: "12px",
                      color: "#EA1010",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.city}
                  </label>
                )}
              </Form.Group>
            </WheelSectionCard>
          </Col>

          {/* RIGHT COLUMN */}
          <Col lg={6} md={6} style={{ paddingRight: 0 }}>
            {/* Presentation */}
            <WheelSectionCard title="Presentation">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <WheelPreview
                  sliceColors={SLICE_COLORS}
                  displayedSlices={displayedSlices}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#F9F9F9",
                  borderRadius: "10px",
                  padding: "10px 16px",
                }}
              >
                <label style={{ fontSize: "14px", color: neturalBlack }}>
                  Displayed slices
                </label>
                <Form.Select
                  value={displayedSlices}
                  onChange={(e) =>
                    handleDisplayedSlicesChange(Number(e.target.value))
                  }
                  style={{
                    width: "80px",
                    borderRadius: "8px",
                    border: `1px solid ${primaryGrey}`,
                  }}
                >
                  {DISPLAYED_SLICES_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </WheelSectionCard>

            {/* Reward Type */}
            {/* <WheelSectionCard title="Reward Type">
              <RewardTypePicker
                selectedType={rewardType}
                onTypeChange={setRewardType}
              />
            </WheelSectionCard> */}

            {/* Slice Configuration */}
            <WheelSectionCard
              title="Slice Configuration"
              subtitle="Choose which product can be rewarded and set the discount if it gets selected."
            >
              <DuplicateToggle
                checked={duplicateSlice}
                onChange={handleDuplicateToggle}
                disabled={calculateDuplicateDisabled()}
              />

              {errors.duplicateNoProduct && (
                <label
                  style={{
                    fontSize: "12px",
                    color: "#EA1010",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  {errors.duplicateNoProduct}
                </label>
              )}

              {slices.slice(0, displayedSlices).map((slice, index) => (
                <SliceConfigurationRow
                  key={index}
                  sliceNumber={index + 1}
                  color={SLICE_COLORS[index % SLICE_COLORS.length]}
                  productSearch={slice.productSearch}
                  discount={slice.discount}
                  onProductSearchChange={(val) =>
                    handleSliceChange(index, "productSearch", val)
                  }
                  onDiscountChange={(val) =>
                    handleSliceChange(index, "discount", val)
                  }
                  onProductInputClick={() => setModalSliceIndex(index)}
                  disabled={duplicateSlice && index >= patternLength}
                  discountDisabled={rewardType === "Discount" && isZeroPricedSlice(slice)}
                  showDiscount={rewardType === "Discount"}
                  productError={errors.slices?.[index]?.productSearch}
                  discountError={errors.slices?.[index]?.discount}
                />
              ))}
            </WheelSectionCard>
          </Col>
        </Row>

        <ProductSelectionModal
          show={modalSliceIndex !== null}
          onHide={() => setModalSliceIndex(null)}
          onProductSelected={(selected) =>
            handleProductSelected(modalSliceIndex, selected, () =>
              setModalSliceIndex(null),
            )
          }
        />
      </div>
    </div>
  );
};

export default RewardWheelCreatePage;
